import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { NavigationService } from '@core/navigation/services/navigation.service';

@Injectable({ providedIn: 'root' })
export class BackendAvailabilityService {
  private readonly navigationService = inject(NavigationService);

  private readonly backendUnavailable = signal(false);

  private readonly lastError = signal<HttpErrorResponse | undefined>(undefined);

  /**
   * Determines whether the backend service is currently unavailable.
   *
   * @returns {boolean} True if the backend is unavailable; otherwise, false.
   */
  public isBackendUnavailable(): boolean {
    return this.backendUnavailable();
  }

  /**
   * Marks the backend as unavailable and navigates to the backend unavailable route.
   *
   * @param {HttpErrorResponse} [error] Optional parameter containing error details related to the backend status.
   * @returns {void} This method does not return a value.
   */
  public markUnavailable(error?: HttpErrorResponse): void {
    if (this.backendUnavailable()) {
      return;
    }

    this.backendUnavailable.set(true);
    this.lastError.set(error);

    void this.navigationService.goToBackendUnavailable();
  }

  /**
   * Resets the state of the instance by clearing the last error
   * and marking the backend as available.
   *
   * A transient backend outage is not a logout event, so the persisted session
   * (User, ADP/Hanko tokens, grants, auth source marker) must survive the retry.
   * Wiping localStorage here left the app anonymous after the reload: the
   * backend-unavailable guard redirects `/backend-unavailable` → `/`, whose
   * `roleGuard` blocks forever waiting for `isUserLoggedIn` (which stays false
   * with no hydrated session), so the page never redirected. State is reset via
   * the signals below; storage is intentionally left intact.
   *
   * @returns {void} This method does not return a value.
   */
  public reset(): void {
    this.backendUnavailable.set(false);
    this.lastError.set(undefined);
  }

  /**
   * Retries the current operation by resetting the state and reloading the page.
   * This method resets any necessary internal components or states and refreshes the browser window
   * to retry the operation from a fresh state.
   *
   * @returns {void} Does not return a value.
   */
  public retry(): void {
    this.reset();
    globalThis.location.reload();
  }
}
